import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MessageSquareText, Send, Users } from "lucide-react";
import StudyLayout from "../components/StudyLayout";
import Breadcrumbs from "../components/Breadcrumbs";
import NavigationButtons from "../components/NavigationButtons";
import Button from "../components/Button";
import Spinner from "../components/Spinner";
import { connectionsAPI } from "../services/api";
import { getConnectionLimits } from "../constants/contentLimits";
import { useAuthState } from "../hooks/useAuth";
import { useToasts } from "../hooks/useToasts";
import "./Connections.css";

const formatDate = (value) => {
  if (!value) return "";
  return new Date(value).toLocaleString();
};

const ConnectionDetails = () => {
  const { connectionId } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToasts();
  const { user } = useAuthState();
  const fallbackConnectionLimits = getConnectionLimits(user);
  const [loading, setLoading] = useState(true);
  const [connection, setConnection] = useState(null);
  const [notes, setNotes] = useState([]);
  const [noteBody, setNoteBody] = useState("");
  const [sending, setSending] = useState(false);
  const composeRef = useRef(null);
  const noteTextareaRef = useRef(null);

  const breadcrumbs = useMemo(
    () => [
      { label: "MySpace", path: "/myspace" },
      { label: "Connections", path: "/connections" },
      {
        label: connection?.otherUser?.publicDisplayName || "Connection",
        path: null,
      },
    ],
    [connection],
  );

  const noteBodyLimit =
    connection?.noteBodyLimit || fallbackConnectionLimits.connectionNoteBody;
  const dailyNoteLimit =
    connection?.dailyLimit || fallbackConnectionLimits.connectionDailyNotes;
  const dailyNotesUsed = connection?.dailyUsage?.mine || 0;
  const noteDisabledReason = connection?.disabledReason || "";
  const dailyLimitReached =
    connection?.canSend === false || dailyNotesUsed >= dailyNoteLimit;
  const isAcceptedConnection =
    connection?.connectionType === "connection" &&
    connection?.status === "accepted";
  const conversationLabel = isAcceptedConnection
    ? "Connection"
    : "Conversation";

  useEffect(() => {
    const loadConnection = async () => {
      try {
        const response = await connectionsAPI.getConnection(connectionId);
        setConnection(response.data.connection);
        setNotes(response.data.notes || []);
        if (response.data.connection?.isUnread) {
          await connectionsAPI.markConnectionRead(connectionId);
          window.dispatchEvent(new Event("inbox-changed"));
        }
      } catch (error) {
        addToast(
          error.response?.data?.details?.[0] || "Connection not found",
          "error-warning",
        );
        navigate("/connections");
      } finally {
        setLoading(false);
      }
    };

    loadConnection();
  }, [addToast, connectionId, navigate]);

  const handleSendNote = async () => {
    const trimmed = noteBody.trim();
    if (!trimmed) {
      addToast("Note is required", "error-warning");
      return;
    }

    try {
      setSending(true);
      const response = await connectionsAPI.sendConnectionNote(
        connectionId,
        trimmed,
      );
      setNotes((prev) => [...prev, response.data.note]);
      setConnection((prev) => ({
        ...prev,
        dailyUsage: {
          ...(prev?.dailyUsage || {}),
          mine: response.data.noteIndex || dailyNotesUsed + 1,
        },
      }));
      setNoteBody("");
      addToast("Note sent", "success-info");
      window.dispatchEvent(new Event("inbox-changed"));
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] || "Failed to send note",
        "error-warning",
      );
    } finally {
      setSending(false);
    }
  };

  if (loading) return <Spinner visible={true} />;

  return (
    <StudyLayout
      breadcrumbs={<Breadcrumbs path={breadcrumbs} />}
      buttons={<NavigationButtons />}
    >
      <div className="connections-page">
        <section className="connections-hero">
          <div className="connections-hero-copy">
            <div className="connections-kicker">
              <MessageSquareText size={16} />
              <span>
                {conversationLabel} with{" "}
                {connection?.otherUser?.publicDisplayName || "Asasiasa member"}
              </span>
            </div>
          </div>
        </section>

        <section className="connection-compose" ref={composeRef}>
          <textarea
            ref={noteTextareaRef}
            value={noteBody}
            onChange={(event) => setNoteBody(event.target.value)}
            placeholder="Write a note..."
            maxLength={noteBodyLimit}
            rows="2"
            disabled={dailyLimitReached}
          />
          <div className="connection-compose-actions">
            <div className="connection-compose-limits">
              <span className={dailyLimitReached ? "is-limit-reached" : ""}>
                {noteDisabledReason ||
                  `${noteBody.length}/${noteBodyLimit} · ${dailyNotesUsed}/${dailyNoteLimit} today`}
              </span>
              <span className="connections-limit-note">
                Notes are limited per day and are asynchronous.
              </span>
            </div>
            <Button
              className="connections-secondary-button"
              onClick={handleSendNote}
              disabled={sending || dailyLimitReached}
            >
              <Send size={16} />
              Send note
            </Button>
          </div>
        </section>

        <section className="connection-notes-list">
          {[...notes].reverse().map((note) => (
            <article
              className={`connection-note-card ${
                note.isMine ? "is-mine" : "is-theirs"
              }`}
              key={note._id}
            >
              <div className="connection-note-meta">
                <span>
                  {note.isMine
                    ? "You"
                    : connection?.otherUser?.publicDisplayName}
                </span>
                <span>{formatDate(note.createdAt)}</span>
                {note.noteIndex ? (
                  <span>
                    {note.noteIndex}/
                    {note.noteLimit || dailyNoteLimit}
                  </span>
                ) : null}
              </div>
              {note.contextCommunity ? (
                <div className="connection-note-context">
                  <Users size={14} />
                  {note.contextCommunity.name}
                </div>
              ) : null}
              <p>{note.body}</p>
            </article>
          ))}
        </section>
      </div>
    </StudyLayout>
  );
};

export default ConnectionDetails;
